import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, Users, FileText, Headphones, PenTool, MessageCircle, ArrowRight, Info, Target, Lightbulb } from 'lucide-react';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

const ExamStructurePage = ({ provider = 'telc', level = 'b1' }) => {
  // For demo purposes, using props. In your app, you'll get these from useParams()
  const [examData, setExamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(new Set());

  useEffect(() => {
    const loadExamStructure = async () => {
      try {
        const response = await fetch(`/data/lessons/${provider?.toLowerCase()}-${level?.toLowerCase()}/exam_structure.json`);
        const data = await response.json();
        setExamData(data);
      } catch (error) {
        console.error('Error loading exam structure:', error);
      } finally {
        setLoading(false);
      }
    };

    if (provider && level) {
      loadExamStructure();
    }
  }, [provider, level]);

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const getProviderColor = (provider) => {
    const colors = {
      telc: 'from-blue-600 to-blue-800',
      goethe: 'from-green-600 to-green-800',
      osd: 'from-red-600 to-red-800'
    };
    return colors[provider?.toLowerCase()] || 'from-gray-600 to-gray-800';
  };

  const getSectionIcon = (type) => {
    const icons = {
      reading: FileText,
      listening: Headphones,
      writing: PenTool,
      speaking: MessageCircle,
      grammar: Target
    };
    return icons[type] || FileText;
  };

  const renderContentBlock = (block, index) => {
    switch (block.type) {
      case 'heading':
        const HeadingTag = `h${block.level}`;
        return (
          <HeadingTag
            key={index}
            className={`font-bold mb-4 ${
              block.level === 2 ? 'text-2xl text-gray-800' : 'text-xl text-gray-700'
            }`}
          >
            {block.text}
          </HeadingTag>
        );

      case 'paragraph':
        return (
          <p key={index} className="text-gray-600 mb-4 leading-relaxed">
            {block.text.split('**').map((part, i) =>
              i % 2 === 0 ? part : <strong key={i} className="font-semibold text-gray-800">{part}</strong>
            )}
          </p>
        );

      case 'table':
        return (
          <div key={index} className="overflow-x-auto mb-6">
            <table className="w-full border-collapse bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="bg-gray-50">
                  {block.headers.map((header, i) => (
                    <th key={i} className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    {row.map((cell, j) => (
                      <td key={j} className="border border-gray-200 px-4 py-3 text-gray-600">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'exam_section':
        const sectionId = `section-${index}`;
        const isExpanded = expandedSections.has(sectionId);

        return (
          <div key={index} className="mb-8">
            <div
              className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-xl"
            >
              <div
                className="p-6 cursor-pointer"
                onClick={() => toggleSection(sectionId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-full bg-gradient-to-r ${getProviderColor(provider)} text-white`}>
                      {React.createElement(getSectionIcon('reading'), { size: 24 })}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{block.title}</h3>
                      <p className="text-sm text-gray-500 flex items-center mt-1">
                        <Clock className="w-4 h-4 mr-1" />
                        {block.duration}
                      </p>
                    </div>
                  </div>
                  <ArrowRight
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                  />
                </div>
                <p className="text-gray-600 mt-3">{block.description}</p>
              </div>

              <div className={`transition-all duration-500 ease-in-out ${
                isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
              } overflow-hidden`}>
                <div className="px-6 pb-6 space-y-4">
                  {block.sub_parts?.map((part, partIndex) => (
                    <div key={partIndex} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                      <h4 className="font-semibold text-gray-800 mb-2">{part.title}</h4>
                      <p className="text-gray-600 text-sm mb-3">{part.task}</p>

                      {part.strategy && (
                        <div className="bg-white rounded-md p-3 border border-gray-200">
                          <div className="flex items-center mb-2">
                            <Target className="w-4 h-4 text-blue-500 mr-2" />
                            <span className="font-medium text-gray-700">Strategie</span>
                          </div>
                          <p className="text-sm text-gray-600">{part.strategy}</p>
                        </div>
                      )}

                      {part.tips && (
                        <div className="bg-white rounded-md p-3 border border-gray-200 mt-2">
                          <div className="flex items-center mb-2">
                            <Lightbulb className="w-4 h-4 text-yellow-500 mr-2" />
                            <span className="font-medium text-gray-700">Tipps</span>
                          </div>
                          <p className="text-sm text-gray-600">{part.tips}</p>
                        </div>
                      )}

                      {part.grammar_focus && (
                        <div className="bg-white rounded-md p-3 border border-gray-200 mt-2">
                          <div className="flex items-center mb-2">
                            <Info className="w-4 h-4 text-green-500 mr-2" />
                            <span className="font-medium text-gray-700">Grammatik-Fokus</span>
                          </div>
                          <p className="text-sm text-gray-600">{part.grammar_focus}</p>
                        </div>
                      )}

                      {part.vocabulary_focus && (
                        <div className="bg-white rounded-md p-3 border border-gray-200 mt-2">
                          <div className="flex items-center mb-2">
                            <Info className="w-4 h-4 text-purple-500 mr-2" />
                            <span className="font-medium text-gray-700">Wortschatz-Fokus</span>
                          </div>
                          <p className="text-sm text-gray-600">{part.vocabulary_focus}</p>
                        </div>
                      )}

                      {part.redemittel && (
                        <div className="bg-white rounded-md p-3 border border-gray-200 mt-2">
                          <div className="flex items-center mb-2">
                            <MessageCircle className="w-4 h-4 text-indigo-500 mr-2" />
                            <span className="font-medium text-gray-700">Redemittel</span>
                          </div>
                          <div className="space-y-1">
                            {part.redemittel.map((phrase, i) => (
                              <span key={i} className="inline-block bg-gray-100 text-xs px-2 py-1 rounded mr-2 mb-1">
                                {phrase}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {part.functions && (
                        <div className="bg-white rounded-md p-3 border border-gray-200 mt-2">
                          <div className="flex items-center mb-2">
                            <MessageCircle className="w-4 h-4 text-indigo-500 mr-2" />
                            <span className="font-medium text-gray-700">Sprachfunktionen</span>
                          </div>
                          <div className="space-y-1">
                            {part.functions.map((func, i) => (
                              <div key={i} className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                {func}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {block.evaluation_criteria && (
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <CheckCircle className="w-4 h-4 text-yellow-600 mr-2" />
                        Bewertungskriterien
                      </h4>
                      <ul className="space-y-2">
                        {block.evaluation_criteria.map((criterion, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <span>{criterion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {block.structure_template && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <FileText className="w-4 h-4 text-blue-600 mr-2" />
                        Struktur-Vorlage
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(block.structure_template).map(([key, value]) => (
                          <div key={key} className="flex items-start">
                            <span className="font-medium text-sm text-blue-700 w-24 flex-shrink-0">{key}:</span>
                            <span className="text-sm text-gray-600">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {block.grammar_targets && (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <Target className="w-4 h-4 text-green-600 mr-2" />
                        Grammatik-Ziele
                      </h4>
                      <div className="space-y-1">
                        {block.grammar_targets.map((target, i) => (
                          <span key={i} className="inline-block bg-green-100 text-xs px-2 py-1 rounded mr-2 mb-1 text-green-800">
                            {target}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'tip':
        return (
          <div key={index} className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 border-l-4 border-yellow-400 mb-6 shadow-sm">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 text-yellow-500 mr-2" />
              {block.title}
            </h4>
            <p className="text-gray-600 leading-relaxed">{block.content}</p>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LogamiLogoSpinner size="2xl" className="mx-auto" decorative={false} alt="Logami loading" />
          <p className="mt-4 text-gray-600">Lade Prüfungsstruktur...</p>
        </div>
      </div>
    );
  }

  if (!examData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Prüfungsstruktur nicht gefunden.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${getProviderColor(provider)} text-white py-12`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{examData.title}</h1>
            <p className="text-xl opacity-90">
              {provider?.toUpperCase()} {level?.toUpperCase()} - Vollständige Prüfungsstruktur
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {examData.contentBlocks?.map((block, index) => renderContentBlock(block, index))}
        </div>
      </div>
    </div>
  );
};

export default ExamStructurePage;